/**
 * texto.ts — Categoria "Texto" (10 modelos premium)
 *
 * Parágrafos, listas, citações e blocos de destaque para páginas
 * institucionais, propostas e landings.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote C)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { WEB, b } from './_shared';

const PARAGRAFO =
  'Explique aqui o que a sua empresa faz, para quem faz e qual problema resolve. ' +
  'Escreva com frases curtas e evite termos técnicos que o cliente não usa no dia a dia.';

const modelos: StudioPatternDefinition[] = [
  {
    key: 'texto.paragrafo',
    category: 'texto',
    label: 'Parágrafo simples',
    description: 'Um bloco de texto com boa largura de leitura.',
    preview: 'text',
    outputs: WEB,
    order: 1,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'left', maxWidth: '760px', motion: 'fade' }, [
        b('ui.paragraph', { text: PARAGRAFO, maxWidth: '680px' }),
      ]),
    ],
  },
  {
    key: 'texto.com-titulo',
    category: 'texto',
    label: 'Texto com título',
    description: 'Título de seção seguido de um parágrafo.',
    preview: 'text',
    outputs: WEB,
    order: 2,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'left', maxWidth: '820px', motion: 'fade-up' }, [
        b('ui.title', { text: 'Sobre a empresa', size: 'lg', level: 'h2' }),
        b('ui.paragraph', { text: PARAGRAFO }),
      ]),
    ],
  },
  {
    key: 'texto.duas-colunas',
    category: 'texto',
    label: 'Texto em duas colunas',
    description: 'Dois parágrafos lado a lado.',
    preview: 'grid-2',
    outputs: WEB,
    order: 3,
    build: () => [
      b('section.layered', { layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1080px', motion: 'fade' }, [
        b('ui.paragraph', { text: PARAGRAFO }),
        b('ui.paragraph', { text: 'Complete com o histórico da empresa, os segmentos atendidos e o que faz o seu atendimento ser diferente.' }),
      ]),
    ],
  },
  {
    key: 'texto.destaque',
    category: 'texto',
    label: 'Texto em destaque',
    description: 'Bloco com fundo suave para chamar atenção.',
    preview: 'text',
    outputs: WEB,
    order: 4,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'left', maxWidth: '820px', backgroundColor: '#f8fafc', radius: 'xl', motion: 'fade-up' }, [
        b('ui.paragraph', { text: 'Ponto importante que o cliente precisa ler antes de decidir.', size: 'lg' }),
      ]),
    ],
  },
  {
    key: 'texto.citacao',
    category: 'texto',
    label: 'Citação',
    description: 'Frase em destaque com autoria abaixo.',
    preview: 'text',
    outputs: WEB,
    order: 5,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '820px', motion: 'fade' }, [
        b('ui.title', { text: '“O atendimento resolveu em um dia o que levava uma semana.”', size: 'md', level: 'h3', align: 'center' }),
        b('ui.paragraph', { text: 'Maria Souza — Transportes Souza', align: 'center', size: 'sm' }),
      ]),
    ],
  },
  {
    key: 'texto.lista',
    category: 'texto',
    label: 'Lista de itens',
    description: 'Três pontos curtos em cartões simples.',
    preview: 'list',
    outputs: WEB,
    order: 6,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'left', maxWidth: '860px', layout: 'stack', motion: 'fade-up' }, [
        b('ui.card', { icon: '✓', title: 'Implantação assistida', text: 'Nossa equipe configura tudo com você.', variant: 'outline', radius: 'lg' }),
        b('ui.card', { icon: '✓', title: 'Sem fidelidade', text: 'Você pode cancelar quando quiser.', variant: 'outline', radius: 'lg' }),
        b('ui.card', { icon: '✓', title: 'Suporte humano', text: 'Atendimento por WhatsApp e e-mail.', variant: 'outline', radius: 'lg' }),
      ]),
    ],
  },
  {
    key: 'texto.escuro',
    category: 'texto',
    label: 'Texto em fundo escuro',
    description: 'Bloco escuro para um recado forte.',
    preview: 'text',
    outputs: WEB,
    order: 7,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'lg', align: 'center', maxWidth: '860px', backgroundGradient: 'mesh-dark', glow: 'bottom-left', textColor: '#ffffff', motion: 'fade' },
        [b('ui.paragraph', { text: 'Quem organiza o processo comercial vende mais — e com menos esforço.', size: 'lg', align: 'center' })],
      ),
    ],
  },
  {
    key: 'texto.aviso',
    category: 'texto',
    label: 'Aviso importante',
    description: 'Observação legal ou condição em destaque discreto.',
    preview: 'text',
    outputs: WEB,
    order: 8,
    build: () => [
      b('section.layered', { paddingY: 'sm', align: 'left', maxWidth: '820px' }, [
        b('ui.paragraph', { text: 'Valores sujeitos a alteração sem aviso prévio. Consulte as condições vigentes.', size: 'sm' }),
      ]),
    ],
  },
  {
    key: 'texto.com-selo',
    category: 'texto',
    label: 'Texto com selo',
    description: 'Selo de contexto acima do parágrafo.',
    preview: 'text',
    outputs: WEB,
    order: 9,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'left', maxWidth: '820px', motion: 'fade-up' }, [
        b('ui.badge', { text: 'Nossa história', variant: 'soft' }),
        b('ui.paragraph', { text: PARAGRAFO }),
      ]),
    ],
  },
  {
    key: 'texto.completo',
    category: 'texto',
    label: 'Bloco de conteúdo completo',
    description: 'Título, dois parágrafos, lista e convite.',
    preview: 'text',
    outputs: WEB,
    order: 10,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'left', maxWidth: '900px', motion: 'fade' }, [
        b('ui.title', { text: 'Como trabalhamos', size: 'lg', level: 'h2' }),
        b('ui.paragraph', { text: PARAGRAFO }),
        b('ui.card', { icon: '•', title: 'Responsável dedicado', text: 'Você sempre fala com a mesma pessoa.', variant: 'outline', radius: 'lg' }),
        b('ui.buttons', { items: 'Falar com o time|#formulario|outline' }),
      ]),
    ],
  },
];

registerPatterns(modelos);
